import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import type { Entity } from '@shared/data/entity';
import { TagField } from '@shared/data/entity/fields/TagField';
import { useUpdateWorkflowEntity } from '@shared/scenes/entity/edit/hooks/useUpdateWorkflowEntity';
import { ChecklistTagComponent, getComponentLabel } from '@shared/scenes/workflows/utils';
import { i18n } from '@shared/util/i18n';
import CardStyles from './Card.module.css';
import styles from './ChecklistButton.module.css';

interface ChecklistButtonProps {
  entity: Entity;
  checklistComponent: ChecklistTagComponent;
  viewTemplateId: string;
}

export function ChecklistButton({
  entity,
  checklistComponent,
  viewTemplateId,
}: ChecklistButtonProps) {
  const { updateWorkflowEntity, loading } = useUpdateWorkflowEntity();
  const field = getTagField(entity, checklistComponent);
  const { checkedOption, uncheckedOption } = checklistComponent.checklistOptions;
  const isCompleted = field?.tagIds?.includes(checkedOption);
  // optimistically invert completed state while request is in flight
  const completed = loading ? !isCompleted : isCompleted;
  const iconName = completed ? 'task-filled' : 'task';
  const label = getComponentLabel(checklistComponent);

  const onPress = () => {
    updateWorkflowEntity(
      entity.id,
      { [checklistComponent.id]: isCompleted ? [uncheckedOption] : [checkedOption] },
      viewTemplateId,
    );
  };

  return (
    <View
      stopPropagation
      center
      paddingLeft={1}
      paddingTop={0.5}
      className={CardStyles.checklistButton}
    >
      <Button
        variant="ghost"
        size="medium"
        icon={iconName}
        iconOnly
        iconGridSize={4.5}
        onClick={onPress}
        label={label}
        className={[styles.checklistButton, completed && styles.completed]}
        tooltip={i18n('collectionView.listView.checkList.toggleTooltip', { label })}
      />
    </View>
  );
}

function getTagField(entity: Entity, checklistComponent: ChecklistTagComponent): TagField | null {
  const field = entity.allFields.get(checklistComponent.id);
  if (field instanceof TagField) {
    return field;
  } else {
    return null;
  }
}
